import React from 'react';
import styled from 'styled-components';
import { formatDate } from '../utils/dateFormat';

const ResumeContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 2 rem;
  grid-row-gap: 1rem
  padding: 2rem;
  background: #f8f9fa;
  font-family: ${props => props.styles?.fontFamily || 'Poppins, sans-serif'};
`;

const Header = styled.header`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 1rem;
  margin-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 2rem;
  
  .name-title {
    grid-column: 1 / 6;
    h1 {
      font-size: 3rem;
      margin: 0;
      line-height: 1.2;
      color: ${props => props.styles?.primaryColor || '#ff4081'};
    }
  }
  
  .contact-info {
    grid-column: 6 / -1;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    // 主轴对齐方式
    justify-content: space-start;
    // 侧轴对齐方式
    align-items: space-evenly;
    font-size: 0.9rem;
  }
    span {
      // padding-right: 1rem;
      // width: 150px;
      flex-basis: 150px;
      flex-grow: 0;
      flex-shrink: 0;
    }
`;

const Summary = styled.div`
  grid-column: 1 / -1;
  padding: 2rem;
  background: ${props => props.styles?.primaryColor || '#ff4081'};
  color: white;
  border-radius: 8px;
  margin-bottom: 2rem;
  
  p {
    font-size: 1.1rem;
    margin: 0;
    line-height: 1.6;
  }
`;

const ExperienceSection = styled.div`
  grid-column: 1 / 8;
`;

const EducationSection = styled.div`
  grid-column: 1 / 8;
`;

const Card = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
  
  h3 {
    color: ${props => props.styles?.primaryColor || '#ff4081'};
    margin: 0 0 1rem;
    font-size: 1.2rem;
    border-bottom: 2px solid ${props => `${props.styles?.primaryColor}22` || '#ff408122'};
    padding-bottom: 0.5rem;
  }
  
  .content {
    padding: 0.5rem 0;
  }
  
  .skill-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  
  .education-item {
    margin-bottom: 1.5rem;
    
    &:last-child {
      margin-bottom: 0;
    }
    
    .school-name {
      font-weight: bold;
      margin-bottom: 0.25rem;
    }
    
    .education-details {
      margin-top: 0.5rem;
      
      .gpa {
        color: #666;
        font-size: 0.9em;
        margin: 0.25rem 0;
      }
      
      .courses {
        color: #666;
        font-size: 0.9em;
        margin-top: 0.5rem;
        
        .course-list {
          margin-top: 0.25rem;
          font-style: italic;
        }
      }
    }
  }
  
  .description {
    margin-top: 0.5rem;
    
    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    
    p {
      margin: 0.5rem 0;
    }
    
    li {
      margin: 0.25rem 0;
    }
    
    strong {
      font-weight: 600;
    }
    
    em {
      font-style: italic;
    }
  }
`;

const SkillTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  span {
    padding: 0.5rem 1rem;
    background: ${props => `${props.styles?.primaryColor}11` || '#ff408111'};
    color: ${props => props.styles?.primaryColor || '#ff4081'};
    border-radius: 20px;
    font-size: 0.9rem;
  }
`;

const TimelineDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.styles?.primaryColor || '#ff4081'};
  margin-right: 1rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 12px;
    bottom: -24px;
    width: 2px;
    background: ${props => `${props.styles?.primaryColor}33` || '#ff408133'};
    transform: translateX(-50%);
  }
`;

const SidebarContent = styled.div`
  position: sticky;
  top: 2rem;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  padding-right: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.styles?.primaryColor || '#ff4081'};
    border-radius: 2px;
  }
`;

const SkillLevel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  
  .skill-name {
    flex: 1;
  }
  
  .level-dots {
    display: flex;
    gap: 2px;
    
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: ${props => props.filled ? 
        props.styles?.primaryColor || '#ff4081' : 
        `${props.styles?.primaryColor}22` || '#ff408122'
      };
    }
  }
`;

const CreativeTemplate = ({ data, styles }) => {
  const { personalInfo, education, experience, skills, certificates, technicalSkills, selfEvaluation } = data;

  return (
    <ResumeContainer>
      <Header styles={styles}>
        <div className="name-title">
          <h1>{personalInfo?.name}</h1>
          <div className="job-info">
            {personalInfo?.workYears && (
              <span>{personalInfo.workYears}年工作经验</span>
            )}
            {personalInfo?.jobIntention && (
              <span>· {personalInfo.jobIntention}</span>
            )}
          </div>
        </div>
        <div className="contact-info">
          <span>{personalInfo?.phone}</span>
          <span>{personalInfo?.email}</span>
          {personalInfo?.address && (
            <span>居住城市: {personalInfo.address}</span>
          )}
          {personalInfo?.targetCity && (
            <span>意向城市: {personalInfo.targetCity}</span>
          )}
          {personalInfo?.expectedSalary && (
            <span>期望薪资: {personalInfo.expectedSalary}</span>
          )}
          {personalInfo?.availableDate && (
            <span>可入职时间: {formatDate(personalInfo.availableDate)}</span>
          )}
        </div>
      </Header>

      {/* 自我评价部分 */}
      {selfEvaluation && (
        <Summary styles={styles}>
          <h2>自我评价</h2>
          <p>{selfEvaluation}</p>
        </Summary>
      )}

      <ExperienceSection>
        {experience?.map((exp, index) => (
          <Card key={index} styles={styles}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TimelineDot styles={styles} />
              <div>
                <h3>{exp.position}</h3>
                <div>{exp.company} | {exp.location}</div>
                <div style={{ color: '#666' }}>{formatDate(exp.startDate)} - {formatDate(exp.endDate)}</div>
              </div>
            </div>
            <div 
              className="description"
              dangerouslySetInnerHTML={{ 
                __html: exp.description?.replace(/\n/g, '<br/>') || '' 
              }}
            />
          </Card>
        ))}
      </ExperienceSection>

      <EducationSection>
        <SidebarContent styles={styles}>
          <Card styles={styles}>
            <h3>教育背景</h3>
            <div className="content">
              {education?.map((edu, index) => (
                <div key={index} className="education-item">
                  <div className="school-name">{edu.school}</div>
                  <div>{edu.degree} · {edu.field}</div>
                  <div style={{ color: '#666', fontSize: '0.9em' }}>
                    {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                  </div>
                  {edu.showGpa && edu.gpa && (
                    <div style={{ color: '#666', fontSize: '0.9em', marginTop: '0.5rem' }}>
                      GPA: {edu.gpa}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {technicalSkills?.length > 0 && (
            <Card styles={styles}>
              <h3>技术技能</h3>
              <div className="content">
                {technicalSkills.map((category, index) => (
                  <div key={index} style={{ marginBottom: '1rem' }}>
                    <h4 style={{ color: '#666', marginBottom: '0.5rem' }}>
                      {category.category}
                    </h4>
                    {category.items.map((item, i) => (
                      <SkillLevel key={i} styles={styles}>
                        <span className="skill-name">{item.name}</span>
                        <div className="level-dots">
                          {[1,2,3,4,5].map(level => (
                            <div 
                              key={level} 
                              className="dot"
                              style={{
                                background: level <= item.level ? 
                                  styles?.primaryColor || '#ff4081' : 
                                  `${styles?.primaryColor}22` || '#ff408122'
                              }}
                            />
                          ))}
                        </div>
                      </SkillLevel>
                    ))}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {certificates?.length > 0 && (
            <Card styles={styles}>
              <h3>荣誉证书</h3>
              <div className="content">
                {certificates.map((cert, index) => (
                  <div key={index} style={{ marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 'bold' }}>{cert.name}</div>
                    <div style={{ color: '#666', fontSize: '0.9em' }}>
                      {cert.issuer} · {formatDate(cert.issueDate)}
                    </div>
                    {cert.description && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.9em' }}>
                        {cert.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card styles={styles}>
            <h3>技能特长</h3>
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: Array.isArray(skills) ? skills.join('<br/>') : (skills || '')
              }}
            />
          </Card>
        </SidebarContent>
      </EducationSection>
    </ResumeContainer>
  );
};

export default CreativeTemplate; 